import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import Global from '../../../store/global-selectors';
import { useLocation } from 'react-router-dom';
import DataDrivenLogo from '../../../components/data-driven-logo';
import { areLocalDeliveryServicesAvailableForSeller } from '../../../hooks/use-are-local-delivery-services-available-for-seller';
import {
  ALL_ORDERS_SEARCH_RESULT_VIEW,
  AWAITING_PAYMENT_VIEW,
  AWAITING_SHIPMENT_VIEW,
  CANCELLED_VIEW,
  ON_HOLD_VIEW,
  ORDER_ALERTS_SHIP_TOGETHER_VIEW,
  ORDER_ALERTS_VIEW,
  REJECTED_FULFILLMENT_VIEW,
  SHIPPED_VIEW,
  PENDING_PICKUP_VIEW,
  PENDING_FULFILLMENT_VIEW,
  ADVANCED_SEARCH_VIEW,
  AWAITING_SHIPMENT_ID,
  ORDER_ALERTS_SHIP_TOGETHER_ID,
  ORDER_ALERTS_ID,
  BATCHES_ID,
} from '../orders-page-view-constants';
import { emptyObject } from 'ui-components/default-props/empty';
import { useQuickSearchTerm } from '../../../hooks/routing/use-quick-search-term';
import { hasQuickSearchTerm } from '../../../services/quicksearch-service';

const OrdersLeftNavContext = React.createContext({});

const defaultOrdersGridViews = [
  ALL_ORDERS_SEARCH_RESULT_VIEW,
  AWAITING_PAYMENT_VIEW,
  ON_HOLD_VIEW,
  AWAITING_SHIPMENT_VIEW,
  SHIPPED_VIEW,
  CANCELLED_VIEW,
  {
    ...ORDER_ALERTS_VIEW,
    items: [
      {
        ...ORDER_ALERTS_SHIP_TOGETHER_VIEW,
        theme: 'light',
      },
    ],
  },
];

export const getAllViews = ({
  showPendingPickup,
  showRejectedFulfillment,
  showPendingFulfillment,
  includeAdvancedSearch,
  activeStores = [],
  removeAllSearchResults,
}) => {
  const newViews = cloneDeep(defaultOrdersGridViews);
  if (removeAllSearchResults) {
    newViews.shift();
  }
  if (includeAdvancedSearch) {
    newViews.push(ADVANCED_SEARCH_VIEW);
  }
  // this is a bit weird but we want to insert the new views AFTER awaiting shipment.
  const viewsWithoutStores = newViews.reduce((sum, view) => {
    if (view.id !== AWAITING_SHIPMENT_ID) {
      return [...sum, view];
    }
    const sumWithOptionalsAddedIn = [...sum, view];
    if (showPendingPickup) {
      sumWithOptionalsAddedIn.push(PENDING_PICKUP_VIEW);
    }
    if (showRejectedFulfillment) {
      sumWithOptionalsAddedIn.push(REJECTED_FULFILLMENT_VIEW);
    }
    if (showPendingFulfillment) {
      sumWithOptionalsAddedIn.push(PENDING_FULFILLMENT_VIEW);
    }
    return sumWithOptionalsAddedIn;
  }, []);
  return viewsWithoutStores.map((view) => {
    if (view.items) {
      return view;
    }
    return {
      ...view,
      items: activeStores.map((store) => {
        const { marketplaceId, storeGuid, name } = store;
        return {
          id: `${view.id}-${storeGuid}`,
          parentId: view.id,
          hideSearchPrefix: view.hideSearchPrefix,
          parentLabel: view.label,
          storeGuid,
          label: name,
          theme: 'light',
          shouldRenderAnIcon: true,
          customIcon: <DataDrivenLogo logoType='icon' marketplaceId={Number.parseInt(marketplaceId, 10)} />,
        };
      }),
    };
  });
};

export const getSelectedViewFromPathName = (pathname = '', activeStores, uniqueIdForShipTogether) => {
  let realPathName = `${pathname}`;
  if (realPathName.startsWith('/')) {
    realPathName = realPathName.substr(1);
  }

  if (realPathName.startsWith('orders/batches') || realPathName.startsWith('shipments/batches')) {
    return { id: BATCHES_ID };
  }

  // top level orders page is a valid route and defaults to awaiting shipment
  if (realPathName === 'orders' || realPathName === 'orders/') {
    return AWAITING_SHIPMENT_VIEW;
  }

  const allViews = getAllViews({
    showPendingPickup: true,
    showRejectedFulfillment: true,
    showPendingFulfillment: true,
    includeAdvancedSearch: true,
    activeStores,
  });
  let selectedItem;
  allViews.forEach((view) => {
    if (selectedItem) {
      return;
    }
    if (!view.items) {
      if (realPathName.includes(view.id)) {
        selectedItem = view;
      }
      return;
    }
    view.items.forEach((subItem) => {
      const { parentId, storeGuid } = subItem;
      if (realPathName.includes(parentId) && realPathName.includes(storeGuid)) {
        selectedItem = subItem;
      }
    });
    if (selectedItem) {
      return;
    }
    if (realPathName.includes(view.id)) {
      selectedItem = view;
    }
  });
  if (selectedItem?.id === ORDER_ALERTS_SHIP_TOGETHER_ID && !uniqueIdForShipTogether) {
    return ORDER_ALERTS_ID;
  }
  return selectedItem;
};

export const useSelectedOrdersView = () => {
  const stores = useSelector(Global.selectSellerActiveStores);
  const { pathname } = useLocation();
  return getSelectedViewFromPathName(pathname, stores) || emptyObject;
};
export const useSelectedOrdersViewId = () => {
  const view = useSelectedOrdersView();
  return view?.id || '';
};

export const useTopLevelOrdersViewId = () => {
  const view = useSelectedOrdersView();
  if (view?.parentId) {
    return view?.parentId;
  }
  return view?.id || '';
};

const OrdersLeftNavContextProvider = ({ children }) => {
  const selectedViewId = useSelectedOrdersViewId();

  const fulfillmentProviders = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const stores = useSelector(Global.selectSellerActiveStores);
  const showPendingPickup = useSelector((state) => areLocalDeliveryServicesAvailableForSeller(state, Global.selectSellerServices(state)));
  const showPendingFulfillment =
    fulfillmentProviders.length > 0 || stores.some((store) => store.supportsPendingFulfillment && store.isActive);
  const showRejectedFulfillment =
    fulfillmentProviders.length > 0 || stores.some((store) => store.supportsRejectedFulfillment && store.isActive);
  const statusCountMap = useSelector(Global.selectOrdersNavStatusCountMap);
  const quickSearchTerm = useQuickSearchTerm();
  const quickSearchPerformed = hasQuickSearchTerm(quickSearchTerm);

  const ordersGridViews = useMemo(() => {
    const allViews = getAllViews({
      showPendingPickup,
      showRejectedFulfillment,
      showPendingFulfillment,
      includeAdvancedSearch: false,
      activeStores: stores,
      removeAllSearchResults: !quickSearchPerformed,
    });
    return allViews
      .map((view) => {
        if (view.id === selectedViewId) {
          return {
            ...view,
            selected: true,
            expanded: !!view.items,
          };
        }
        if (view.items) {
          const hasSubItemSelected = view.items.some((item) => item.id === selectedViewId);
          if (!hasSubItemSelected) {
            return view;
          }
          return {
            ...view,
            expanded: true,
            items: view.items.map((item) => {
              if (item.id !== selectedViewId) {
                return item;
              }
              return {
                ...item,
                selected: true,
              };
            }),
          };
        }
        return view;
      })
      .map((view) => {
        if (!view.items) {
          return {
            ...view,
            count: statusCountMap[view.id],
          };
        }
        return {
          ...view,
          count: statusCountMap[view.id],
          items: view.items.map((item) => ({
            ...item,
            count: statusCountMap[item.id],
          })),
        };
      });
  }, [selectedViewId, showPendingFulfillment, showPendingPickup, showRejectedFulfillment, statusCountMap, stores, quickSearchPerformed]);

  const contextValue = useMemo(
    () => ({
      selectedViewId,
      ordersGridViews,
    }),
    [selectedViewId, ordersGridViews],
  );

  return <OrdersLeftNavContext.Provider value={contextValue}>{children}</OrdersLeftNavContext.Provider>;
};

OrdersLeftNavContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { OrdersLeftNavContext, OrdersLeftNavContextProvider };
